<template>
  <div class="px-6 pt-8 relative">
    <!-- Breadcrumb -->
    <div class="mb-6">
      <router-link class="breadcrumb-link" to="/publisher/developers">
        Developers
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-link" v-if="developerDetail">{{ developerDetail.name }}</span>
    </div>

    <!-- Developer Header Section -->
    <div class="section-block mb-6">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <div v-if="developerDetail && developerDetail.avatar" class="DeveloperDetail__avatar" :style="{ backgroundImage: `url('${developerDetail.avatar}')` }"></div>
          <div v-else class="DeveloperDetail__avatar-placeholder">
            <div class="avatar-content">
              <v-icon class="avatar-icon">mdi-account</v-icon>
            </div>
          </div>
          <div class="ml-4">
            <div class="d-flex align-center mb-2">
              <h3 class="DeveloperDetail__name" v-if="developerDetail">{{ developerDetail.name }}</h3>
              <div v-else class="DeveloperDetail__placeholder">
                <the-icon icon="developers" class="mr-2" />
                Developer name not available
              </div>
              <span class="badge verified ml-3" v-if="developerDetail && developerDetail.verified">
                <the-icon icon="verified" />
              </span>
              <span class="badge warn-verified ml-3" v-else-if="developerDetail">
                <the-icon icon="warn-verified" />
              </span>
            </div>
            <div class="d-flex align-center email-label">
              <the-icon icon="email" class="mr-2" />
              <span v-if="developerDetail && developerDetail.email">{{ developerDetail.email }}</span>
              <span v-else class="DeveloperDetail__placeholder">Email not provided</span>
            </div>
          </div>
        </div>
        <v-menu left offset-x v-if="isPublisherAdmin">
          <template v-slot:activator="{ on, attrs }">
            <span class="action-button" v-bind="attrs" v-on="on">
              <the-icon icon="action-vertical" />
            </span>
          </template>
          <v-list dense>
            <v-list-item class="action-item delete" @click="onRemoveDeveloper">
              <the-icon icon="trash-filled" /> Delete
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Developer Information and Location Sections -->
    <v-row class="mb-6">
      <v-col cols="12" md="6">
        <div class="section-block">
          <h6 class="DeveloperDetail__section-heading">Developer Information</h6>
          <div class="d-flex mb-4">
            <span class="DeveloperDetail__label">Company</span>
            <span v-if="developerDetail && developerDetail.company" class="text-sm font-bold block tracking-wide text-gray-600">
              {{ developerDetail.company }}
            </span>
            <span v-else class="DeveloperDetail__placeholder">
              <the-icon icon="developers" class="mr-2" />
              Company name not provided
            </span>
          </div>
          <div class="d-flex mb-4">
            <span class="DeveloperDetail__label">Contact</span>
            <span v-if="developerDetail && developerDetail.name" class="text-sm font-bold block tracking-wide text-gray-600">
              {{ developerDetail.name }}
            </span>
            <span v-else class="DeveloperDetail__placeholder">
              <the-icon icon="developers" class="mr-2" />
              Contact name not available
            </span>
          </div>
          <div class="d-flex mb-4">
            <span class="DeveloperDetail__label">Contact Email</span>
            <span v-if="developerDetail && developerDetail.email" class="text-sm font-bold block tracking-wide text-gray-600">
              {{ developerDetail.email }}
            </span>
            <span v-else class="DeveloperDetail__placeholder">
              <the-icon icon="email" class="mr-2" />
              Email address not provided
            </span>
          </div>
          <div class="d-flex mb-4">
            <span class="DeveloperDetail__label">Country</span>
            <span v-if="developerDetail && countryLabel" class="text-sm font-bold block tracking-wide text-gray-600">
              {{ countryLabel }}
            </span>
            <span v-else class="DeveloperDetail__placeholder">
              <the-icon icon="developers" class="mr-2" />
              Country not specified
            </span>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="section-block">
          <developer-location-map :developer-country="developerDetail?.country" />
        </div>
      </v-col>
    </v-row>

    <!-- Developer Statistics Section -->
    <div class="section-block mb-6">
      <h6 class="DeveloperDetail__section-heading">Developer Statistics</h6>
      <div v-if="developerDetail && (developerDetail.appsList?.length > 0 || developerDetail.plugins_count > 0)" class="d-flex justify-center py-4">
        <div class="mr-6 p-3 text-center">
          <div class="DeveloperDetail__statistics-label">Total Plugins</div>
          <div class="DeveloperDetail__statistics-value">
            {{ developerDetail.appsList?.length || developerDetail.plugins_count || 0 }}
          </div>
        </div>
        <div class="mr-6 p-3 text-center">
          <div class="DeveloperDetail__statistics-label">Total Installs</div>
          <div class="DeveloperDetail__statistics-value">
            {{ totalInstalls }}
          </div>
        </div>
        <div class="p-3 text-center">
          <div class="DeveloperDetail__statistics-label">Average Rating</div>
          <div class="DeveloperDetail__statistics-value">
            <v-rating
              v-if="averageRating > 0"
              v-model="averageRating"
              readonly
              background-color="red lighten-3"
              color="red"
              small
              dense
            ></v-rating>
            <span v-else>N/A</span>
          </div>
        </div>
      </div>
      <div v-else class="DeveloperDetail__placeholder">
        <the-icon icon="bar-chart" class="mr-2" />
        No plugins found - statistics not available
      </div>
    </div>

    <!-- Plugins Section -->
    <div class="section-block mb-6">
      <div class="d-flex justify-space-between align-center mb-4">
        <h6 class="DeveloperDetail__section-heading">
          Plugins from {{ developerDetail ? developerDetail.name : 'Developer' }}
        </h6>
        <span v-if="developerDetail && (developerDetail.appsList?.length > 0 || developerDetail.plugins_count > 0)" class="text-sm text-gray-600">
          {{ developerDetail.appsList?.length || developerDetail.plugins_count || 0 }} plugins
        </span>
      </div>
      
      <div v-if="developerDetail && developerDetail.appsList && developerDetail.appsList.length > 0">
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="2"
            v-for="app in developerDetail.appsList"
            :key="app.slug"
            class="mb-3"
          >
            <v-card class="DeveloperDetail__plugin-card" @click="onNavigateToPlugin(app)">
              <div class="image-wrapper">
                <div
                  v-if="hasAppImage(app)"
                  class="image"
                  :style="{ backgroundImage: getAppImage(app) }"
                ></div>
                <div v-else class="image-placeholder">
                  <div class="placeholder-content">
                    <the-icon icon="logo" class="placeholder-logo" />
                    <span class="placeholder-text">No Image Available</span>
                  </div>
                </div>
                
                <!-- Status Badge -->
                <div v-if="getPluginStatus(app)" class="status-badge" :class="getStatusBadgeClass(app)">
                  {{ getPluginStatus(app) }}
                </div>
                
                <!-- Plugin Type Badge -->
                <div class="plugin-type-badge">
                  <the-icon icon="logo-small" width="16" height="16" class="mr-1" />
                  Plugin
                </div>
              </div>
              
              <div class="pa-3">
                <div class="d-flex justify-space-between items-center mb-2">
                  <div class="app-name">
                    <span v-if="app.name">{{ app.name }}</span>
                    <span v-else class="missing-info">Plugin name required</span>
                  </div>
                  <v-menu offset-x>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn text icon compact v-on="on" v-bind="attrs" @click.stop>
                        <the-icon icon="more-vert" />
                      </v-btn>
                    </template>
                    <v-list dark class="the-app-bar__dropdown">
                      <v-list-item @click="onNavigateToPlugin(app)">
                        <v-list-item-title class="SiteApp__action">
                          <span class="SiteApp__icon">
                            <v-icon>visibility</v-icon>
                          </span>
                          View Plugin
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
                
                <!-- Description Section -->
                <div class="app-description mb-3">
                  <div
                    v-if="app.developerContent && app.developerContent.description"
                    v-html="app.developerContent.description"
                  ></div>
                  <div v-else class="missing-info">
                    <the-icon icon="warn" class="warning-icon" />
                    <span>Description required</span>
                  </div>
                </div>
                
                <!-- Footer Section -->
                <div class="d-flex justify-space-between align-center">
                  <div class="d-flex align-center">
                    <div v-if="app.developerData && app.developerData.rating">
                      <v-rating
                        v-model="app.developerData.rating"
                        readonly
                        background-color="red lighten-3"
                        color="red"
                        small
                        dense
                      />
                    </div>
                    <div v-else class="missing-info small">
                      <the-icon icon="warn" class="warning-icon-small" />
                      <span>No rating</span>
                    </div>
                  </div>
                  
                  <div class="d-flex align-center">
                    <the-icon
                      class="mr-2"
                      color="y1"
                      icon="download"
                      width="16"
                      height="16"
                    />
                    <span v-if="app.developerData && app.developerData.installsCount > 0">
                      {{ app.developerData.installsCount }}
                    </span>
                    <span v-else class="missing-info small">0</span>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
      
      <div v-else class="DeveloperDetail__placeholder">
        <the-icon icon="plugins" class="mr-2" />
        No plugins found for this developer
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { MODAL_ALERT } from '@/components/Modals'
import { getCountryLabel } from '@/utils/common'
import DeveloperLocationMap from './DeveloperLocationMap.vue'

export default {
  name: 'DeveloperDetail',
  components: {
    DeveloperLocationMap,
  },
  data() {
    return {
      developerDetail: null,
      loading: false,
      loadingInstalls: false,
      currentDeveloperId: null, // Track current developer to prevent stale updates
    }
  },
  computed: {
    ...mapState({
      currentPublisherInfo: ({ publisher_infos }) => publisher_infos.current,
    }),
    isPublisherAdmin() {
      return this.currentPublisherInfo.role === 'admin'
    },
    countryLabel() {
      return getCountryLabel(this.developerDetail?.country)
    },
    totalInstalls() {
      if (!this.developerDetail || !this.developerDetail.appsList) return 0
      return this.developerDetail.appsList.reduce((total, app) => {
        return total + (app.developerData?.installsCount || 0)
      }, 0)
    },
    averageRating() {
      if (!this.developerDetail || !this.developerDetail.appsList) return 0
      const appsWithRating = this.developerDetail.appsList.filter(app => 
        app.developerData && app.developerData.rating
      )
      if (appsWithRating.length === 0) return 0
      const totalRating = appsWithRating.reduce((sum, app) => sum + app.developerData.rating, 0)
      return Math.round((totalRating / appsWithRating.length) * 10) / 10
    },
  },
  mounted() {
    this.getDeveloperDetail()
  },
  watch: {
    // Watch for route parameter changes (when switching developers)
    '$route.params.developerId': {
      handler(newId, oldId) {
        if (newId !== oldId) {
          // Reset state when switching developers
          this.developerDetail = null
          this.loadingInstalls = false
          // Fetch new developer data
          this.getDeveloperDetail()
        }
      },
      immediate: false,
    },
  },
  methods: {
    /**
     * Map marketplace developer to legacy developerDetail format
     */
    mapDeveloperToLegacyFormat(developer) {
      return {
        id: developer.id,
        name: developer.name || '',
        email: developer.email || '',
        company: developer.company || '',
        website: developer.website || '',
        verified: developer.verified || false,
        status: developer.status || 'active',
        user_id: developer.user_id || null,
        country: developer.country || null,
        avatar: developer.avatar || developer.avatar_url || null,
        appsList: [], // Will be populated separately
        plugins_count: developer.plugins_count || 0, // Store plugins_count from API
        created_at: developer.created_at,
        updated_at: developer.updated_at,
      }
    },
    
    /**
     * Map plugin from API response to legacy format
     * Handles both full plugin objects and simplified plugin objects from developer.plugins array
     */
    mapPluginToLegacyFormat(plugin) {
      // Extract developer info - API returns developer as object
      const developerId = plugin.developer?.id || plugin.developer_id
      
      return {
        id: plugin.id,
        name: plugin.name,
        slug: plugin.slug,
        url: plugin.entrypoint_url || plugin.url || '',
        kind: plugin.kind || 'Site', // Default to Site if not specified
        pluginKind: plugin.kind || 'Site', // For plugin type icons
        developer: developerId,
        developerData: {
          id: plugin.id,
          status: plugin.status === 'active' ? 'Ready for Sale' : (plugin.status || 'draft'),
          installsCount: plugin.installs_count || plugin.installsCount || 0,
          rating: plugin.rating || 0,
          installParams: plugin.install_params ? JSON.stringify(plugin.install_params) : '[]',
          sandboxPermissions: plugin.sandbox_permissions || plugin.sandboxPermissions || [],
          // Add other fields as needed
        },
        developerContent: {
          keyImage: plugin.key_image_url || plugin.thumbnail_url || plugin.image_url || plugin.screenshot_url || null,
          description: plugin.description || '',
          featured: plugin.featured || false,
          categories: plugin.categories || [],
          // Add other content fields as needed
        },
        // Include full plugin data for compatibility
        ...plugin,
      }
    },
    
    /**
     * Get developer detail and their plugins
     */
    async getDeveloperDetail() {
      this.loading = true
      const developerId = this.$route.params.developerId
      
      // Store current developer ID to prevent stale updates
      this.currentDeveloperId = developerId
      
      // Reset developer detail immediately to clear previous state
      this.developerDetail = null
      
      try {
        const marketplaceAPI = (await import('@/services/marketplace')).default
        
        // Fetch developer details
        const developerResponse = await marketplaceAPI.getDeveloper(developerId)
        
        // Handle API response structure: { developers: [...] } or { developer: {...} } or direct object
        let developer = null
        if (developerResponse.developers && developerResponse.developers.length > 0) {
          developer = developerResponse.developers[0]
        } else if (developerResponse.developer) {
          developer = developerResponse.developer
        } else {
          developer = developerResponse
        }
        
        if (!developer) {
          console.error('Developer not found')
          this.loading = false
          return
        }
        
        // Check if we're still on the same developer (prevent race conditions)
        if (this.currentDeveloperId !== developerId) {
          // User switched developers, ignore this response
          return
        }
        
        // Map developer to legacy format
        this.developerDetail = this.mapDeveloperToLegacyFormat(developer)
        
        // Use plugins from API response if available, otherwise fetch separately
        let plugins = []
        if (developer.plugins && Array.isArray(developer.plugins) && developer.plugins.length > 0) {
          // Use plugins from the API response
          plugins = developer.plugins
          
          // If plugins have limited data, fetch full details for each plugin
          // Check if plugins have installs_count - if not, they might need full details
          const needsFullDetails = plugins.some(p => p.installs_count === undefined)
          
          if (needsFullDetails) {
            try {
              // Fetch full plugin details to get installs_count and other data
              const fullPlugins = await marketplaceAPI.listPlugins({ developer_id: developer.id })
              // Filter out plugins that don't belong to this developer (workaround for API bug)
              const filteredPlugins = fullPlugins.filter(p => {
                const pluginDeveloperId = p.developer_id || p.developer?.id
                return pluginDeveloperId === developer.id
              })
              
              // Log warning if API returned plugins from other developers
              if (fullPlugins.length !== filteredPlugins.length) {
                console.warn(
                  `API bug: listPlugins(developer_id=${developer.id}) returned ${fullPlugins.length} plugins, ` +
                  `but only ${filteredPlugins.length} belong to developer ${developer.id}. ` +
                  `Filtered out ${fullPlugins.length - filteredPlugins.length} incorrect plugins.`
                )
              }
              
              // Create a map of full plugin data by id
              const fullPluginMap = new Map(filteredPlugins.map(p => [p.id, p]))
              // Merge full plugin data with simplified plugin data
              plugins = plugins.map(plugin => {
                const fullPlugin = fullPluginMap.get(plugin.id)
                return fullPlugin || plugin
              }).filter(plugin => {
                // Also filter merged plugins to ensure they belong to this developer
                const pluginDeveloperId = plugin.developer_id || plugin.developer?.id
                return pluginDeveloperId === developer.id
              })
            } catch (error) {
              console.warn('Could not fetch full plugin details, using simplified plugin data:', error)
            }
          } else {
            // Filter plugins from API response to ensure they belong to this developer
            plugins = plugins.filter(plugin => {
              const pluginDeveloperId = plugin.developer_id || plugin.developer?.id
              return pluginDeveloperId === developer.id
            })
          }
        } else {
          // Fallback: fetch developer's plugins separately
          try {
            const allPlugins = await marketplaceAPI.listPlugins({ developer_id: developer.id })
            // Filter out plugins that don't belong to this developer (workaround for API bug)
            plugins = allPlugins.filter(plugin => {
              const pluginDeveloperId = plugin.developer_id || plugin.developer?.id
              return pluginDeveloperId === developer.id
            })
            
            // Log warning if API returned plugins from other developers
            if (allPlugins.length !== plugins.length) {
              console.warn(
                `API bug: listPlugins(developer_id=${developer.id}) returned ${allPlugins.length} plugins, ` +
                `but only ${plugins.length} belong to developer ${developer.id}. ` +
                `Filtered out ${allPlugins.length - plugins.length} incorrect plugins.`
              )
            }
          } catch (error) {
            console.warn('Could not fetch developer plugins:', error)
            plugins = []
          }
        }
        
        // Check again if we're still on the same developer before updating
        if (this.currentDeveloperId !== developerId) {
          // User switched developers, ignore this response
          return
        }
        
        // Map plugins to legacy format
        this.developerDetail.appsList = plugins.map(plugin => this.mapPluginToLegacyFormat(plugin))
        
        // Fetch install counts asynchronously for all plugins
        this.loadInstallsData()
      } catch (error) {
        console.error('getDeveloperDetail error:', error)
        // Only reset if we're still on the same developer
        if (this.currentDeveloperId === developerId) {
          this.developerDetail = null
        }
      } finally {
        // Only update loading state if we're still on the same developer
        if (this.currentDeveloperId === developerId) {
          this.loading = false
        }
      }
    },
    
    /**
     * Load install counts for all plugins asynchronously
     * Fetches all installations for the developer's plugins and updates install counts
     */
    async loadInstallsData() {
      if (!this.developerDetail || !this.developerDetail.appsList || this.developerDetail.appsList.length === 0) {
        return
      }
      
      // Store the developer ID we're fetching for to prevent stale updates
      const developerId = this.currentDeveloperId
      const pluginIds = this.developerDetail.appsList.map(plugin => plugin.id)
      
      if (!developerId || pluginIds.length === 0) {
        return
      }
      
      this.loadingInstalls = true
      
      try {
        const marketplaceAPI = (await import('@/services/marketplace')).default
        
        // Fetch all installations for all plugins in parallel (one call per plugin)
        // This is more efficient than fetching all installations and filtering
        const installPromises = pluginIds.map(async (pluginId) => {
          try {
            const installations = await marketplaceAPI.listInstallations({
              plugin_id: pluginId,
            })
            
            // Count active installations
            const activeInstalls = installations.filter(inst => inst.status === 'active').length
            
            return { pluginId, installsCount: activeInstalls }
          } catch (error) {
            console.warn(`Could not fetch installs for plugin ${pluginId}:`, error)
            return { pluginId, installsCount: 0 }
          }
        })
        
        // Wait for all install counts to be fetched
        const installCounts = await Promise.all(installPromises)
        
        // Check if we're still on the same developer before updating
        if (this.currentDeveloperId !== developerId || !this.developerDetail) {
          // User switched developers, ignore this response
          return
        }
        
        // Create a map of plugin ID to install count
        const installCountMap = new Map(installCounts.map(ic => [ic.pluginId, ic.installsCount]))
        
        // Update each plugin's install count
        this.developerDetail.appsList.forEach(plugin => {
          const installsCount = installCountMap.get(plugin.id) || 0
          if (plugin.developerData) {
            plugin.developerData.installsCount = installsCount
          } else {
            plugin.developerData = {
              installsCount: installsCount,
            }
          }
        })
        
        // Force reactivity update to refresh Statistics section
        this.$forceUpdate()
      } catch (error) {
        console.warn('Could not fetch installs data:', error)
      } finally {
        // Only update loading state if we're still on the same developer
        if (this.currentDeveloperId === developerId) {
          this.loadingInstalls = false
        }
      }
    },
    async onRemoveDeveloper() {
      const developerId = this.$route.params.developerId
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Remove Developer',
          text: `You are going to remove ${this.developerDetail.name}. Are you sure?`,
          actionBtnLabel: 'Remove',
          showCancelBtn: true,
          action: async () => {
            try {
              const marketplaceAPI = (await import('@/services/marketplace')).default
              await marketplaceAPI.deleteDeveloper(developerId)
              this.$router.push({ name: 'developer_list' })
            } catch (error) {
              console.error('Error removing developer:', error)
              // Show error message to user
              this.$store.commit('application/openModal', {
                component: MODAL_ALERT,
                props: {
                  title: 'Error',
                  text: error.message || 'Failed to remove developer',
                  actionBtnLabel: 'OK',
                },
              })
            }
          },
        },
      })
    },
    getAppImage(app) {
      let src = ''
      if (app.developerContent) {
        if (app.developerContent.keyImage) src = app.developerContent.keyImage
        else if (
          app.developerContent.screenshots &&
          app.developerContent.screenshots[0]
        )
          src = app.developerContent.screenshots[0]
      }

      return src ? `url('${src}')` : `url("/no-image.jpg")`
    },
    hasAppImage(app) {
      const imageUrl = this.getAppImage(app)
      return imageUrl !== 'url("/no-image.jpg")'
    },
    getPluginStatus(app) {
      // Simple status logic - can be enhanced based on your requirements
      if (!app.name) return 'Draft'
      if (!app.developerContent?.description) return 'Incomplete'
      if (!app.developerData?.status || app.developerData.status === 'Need Review') return 'Pending'
      return null
    },
    getStatusBadgeClass(app) {
      const status = this.getPluginStatus(app)
      if (status === 'Draft') return 'status-draft'
      if (status === 'Incomplete') return 'status-incomplete'
      if (status === 'Pending') return 'status-pending'
      return ''
    },
    onNavigateToPlugin(plugin_detail) {
      this.$router.push({
        name: 'plugin_detail',
        params: { pluginId: plugin_detail.slug },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.section-block {
  border: 1px solid $N5;
  background: $N4;
  border-radius: 4px;
  padding: 24px;
}

.breadcrumb-link {
  text-decoration: none;
  font-size: 15px;
}

.breadcrumb-separator {
  margin-left: 5px;
  margin-right: 5px;
}
h5 {
  font-size: 20px;
  line-height: 24px;
  margin-right: 8px;
}
.badge {
  padding: 8px;
  margin-left: 12px;
  position: relative;
  &:before {
    position: absolute;
    border-radius: 28px;
    opacity: 0.1;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: block;
    content: '';
  }
  &.verified {
    &:before {
      background: $G1;
    }
    svg {
      fill: $G1;
      color: $G1;
    }
  }

  &.warn-verified {
    &:before {
      background: $Y1;
    }
    svg {
      fill: $Y1;
      color: $Y1;
    }
  }
}

.email-label {
  color: $N2;
  svg {
    fill: $N2;
    color: $N2;
  }
}

.action-button {
  svg {
    color: $N28;
    fill: $N28;
  }
}
.action-item {
  display: flex;
  align-items: center;
  width: 120px;
  cursor: pointer;
  &.view {
    svg {
      color: white;
      fill: white;
      margin-right: 5px;
    }
  }
  &.delete {
    color: $R1 !important;
    svg {
      color: $R1;
      fill: $R1;
      margin-right: 5px;
    }
  }
}

$style: DeveloperDetail;
.#{$style} {
  &__avatar {
    width: 64px;
    height: 64px;
    border-radius: 32px;
    background: $Y1;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border: 2px solid rgba($N2, 0.2);
  }

  &__avatar-placeholder {
    width: 64px;
    height: 64px;
    border-radius: 32px;
    background: linear-gradient(135deg, $N4 0%, $N7 100%);
    border: 2px dashed rgba($N2, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(73, 109, 219, 0.1), transparent);
      animation: shimmer 2s infinite;
    }
    
    .avatar-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 1;
      position: relative;
    }
    
    .avatar-icon {
      font-size: 32px !important;
      color: $N2 !important;
      opacity: 0.7;
    }
  }

  &__name {
    font-size: 20px;
    line-height: 24px;
    color: white;
    margin: 0;
  }

  &__label {
    width: 150px;
    flex-shrink: 0;
    color: $N28;
    font-size: 14px;
  }

  &__section-heading {
    color: white;
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 24px;
    font-weight: normal;
  }

  &__statistics-label {
    font-size: 14px;
    color: $N2;
    margin-bottom: 8px;
  }

  &__statistics-value {
    font-size: 20px;
    font-weight: bold;
    color: white;
  }

  &__placeholder {
    display: flex;
    align-items: center;
    color: $N2;
    font-size: 14px;
    font-style: italic;
    padding: 8px 12px;
    background: rgba($N2, 0.1);
    border: 1px dashed rgba($N2, 0.3);
    border-radius: 4px;
    margin: 4px 0;
    
    .the-icon {
      color: $N2;
      fill: $N2;
      opacity: 0.7;
    }
  }

  &__plugin-card {
    // Glass style border with increased corner radius
    background: rgba(37, 36, 55, 0.9) !important;
    border: 1px solid $N5 !important;
    box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.25) !important;
    backdrop-filter: blur(10px) !important;
    border-radius: 16px !important;
    transition: all 0.3s ease;
    cursor: pointer;
    
    // Override Vuetify's default card styles
    &.v-card {
      background: rgba(37, 36, 55, 0.9) !important;
      border-radius: 16px !important;
    }
    
    &.theme--dark.v-card {
      background: rgba(37, 36, 55, 0.9) !important;
      color: #fff !important;
    }
    
    &.v-sheet.v-card {
      border-radius: 16px !important;
    }
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
      border-color: rgba(73, 109, 219, 0.3) !important;
    }
  }
}

// Plugin card styles (reused from PublisherDashboardPluginCard)
.image-wrapper {
  position: relative;
  margin-bottom: 5px;
  overflow: hidden;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.image {
  width: 100%;
  height: 140px;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.image-placeholder {
  width: 100%;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $N4 0%, $N7 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(73, 109, 219, 0.1), transparent);
    animation: shimmer 2s infinite;
  }
  
  .placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1;
    position: relative;
  }
  
  .placeholder-logo {
    width: 48px;
    height: 48px;
    margin-bottom: 8px;
    opacity: 0.6;
    filter: grayscale(1);
  }
  
  .placeholder-text {
    font-size: 12px;
    font-weight: 500;
    color: $N2;
    text-align: center;
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.status-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  border-radius: 6px;
  padding: 4px 8px;
  color: white;
  font-size: 10px;
  font-weight: 600;
  backdrop-filter: blur(8px);
  
  &.status-draft {
    background: rgba($N5, 0.9);
    border: 1px solid rgba($N6, 0.5);
  }
  
  &.status-incomplete {
    background: rgba($Y1, 0.9);
    border: 1px solid rgba($Y1, 0.3);
  }
  
  &.status-pending {
    background: rgba($B1, 0.9);
    border: 1px solid rgba($B1, 0.3);
  }
}

.plugin-type-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  border-radius: 6px;
  padding: 4px 6px;
  background: rgba($B5, 0.9);
  border: 1px solid rgba($B5, 0.3);
  display: flex;
  align-items: center;
  color: white;
  font-size: 10px;
  font-weight: 600;
  backdrop-filter: blur(8px);
}

.app-name {
  font-size: 14px;
  font-weight: 600;
  color: $white;
  line-height: 1.3;
}

.app-description {
  min-height: 40px;
  max-height: 60px;
  white-space: pre-line;
  color: $N2;
  line-height: 1.4;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.missing-info {
  display: flex;
  align-items: center;
  color: $E1;
  font-size: 12px;
  font-weight: 500;
  
  &.small {
    font-size: 10px;
    color: $Y1;
  }
  
  .warning-icon {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
  
  .warning-icon-small {
    width: 12px;
    height: 12px;
    margin-right: 4px;
  }
}

.SiteApp__icon {
  width: 30px;
  text-align: center;
  .v-icon.v-icon {
    font-size: 20px;
  }
}

.SiteApp__action {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  &.dangerous {
    color: $Y1;
  }
}
</style>
